import { Injectable } from '@angular/core';
import * as FileSaver from 'file-saver';
import * as XLSX from 'xlsx';
import { Workbook } from 'exceljs';

const EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
const EXCEL_EXTENSION = '.xlsx';

// A single run of text within a cell, allowing bold/italic segments in one cell
// (used to reproduce the mixed-weight instruction cells in the access-control
// template).
export interface StyledTextRun {
  text: string;
  bold?: boolean;
  italic?: boolean;
}

// A fully-styled cell for template-shaped exports. Colours are ARGB hex strings
// (e.g. 'FF0070C0'); border sides are ExcelJS border style names ('thin' |
// 'medium' | ...). Either `value` or `runs` carries the content.
export interface StyledCell {
  value?: string | number | null;
  runs?: StyledTextRun[];
  fill?: string;
  fontColor?: string;
  bold?: boolean;
  italic?: boolean;
  fontSize?: number;
  fontName?: string;
  horizontal?: 'left' | 'center' | 'right';
  vertical?: 'top' | 'middle' | 'bottom';
  wrapText?: boolean;
  border?: { top?: string; bottom?: string; left?: string; right?: string };
}

export interface StyledSheetOptions {
  sheetName?: string;
  merges?: string[];              // e.g. ['B1:D3', 'G1:J2']
  colWidths?: number[];           // per-column widths (Excel width units)
  rowHeights?: (number | undefined)[];  // per-row heights (points)
}

@Injectable({
  providedIn: 'root'
})
export class ExcelService {

  constructor() { }

  public exportAsExcelFile(json: any[], excelFileName: string): void {
    const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(json);
    const workbook: XLSX.WorkBook = { Sheets: { 'data': worksheet }, SheetNames: ['data'] };
    const excelBuffer: any = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    this.saveAsExcelFile(excelBuffer, excelFileName);

  }

  // Exports a fully-styled sheet (fonts, fills, borders, alignment, merges,
  // column widths, row heights) using ExcelJS. Unlike the community `xlsx`
  // build, this preserves cell styling, so it can reproduce human-authored
  // templates such as the venue Access Control config.
  public async exportStyledSheet(
    rows: StyledCell[][],
    excelFileName: string,
    opts: StyledSheetOptions = {}
  ): Promise<void> {
    const workbook = new Workbook();
    const sheet = workbook.addWorksheet(opts.sheetName || 'Sheet1');

    rows.forEach((row, r) => {
      row.forEach((cellDef, c) => {
        this.applyStyledCell(sheet.getCell(r + 1, c + 1), cellDef);
      });
    });

    (opts.merges || []).forEach((range) => sheet.mergeCells(range));

    (opts.colWidths || []).forEach((width, i) => {
      if (width != null) {
        sheet.getColumn(i + 1).width = width;
      }
    });

    (opts.rowHeights || []).forEach((height, i) => {
      if (height != null) {
        sheet.getRow(i + 1).height = height;
      }
    });

    const buffer = await workbook.xlsx.writeBuffer();
    this.saveAsExcelFile(buffer, excelFileName);
  }

  // Writes one StyledCell's content and formatting onto an ExcelJS cell.
  private applyStyledCell(cell: import('exceljs').Cell, def: StyledCell): void {
    if (def.runs?.length) {
      cell.value = {
        richText: def.runs.map((run) => ({
          text: run.text,
          font: {
            bold: run.bold,
            italic: run.italic,
            size: def.fontSize ?? 11,
            name: def.fontName ?? 'Calibri',
            color: def.fontColor ? { argb: def.fontColor } : undefined
          }
        }))
      };
    } else {
      cell.value = def.value ?? null;
    }

    cell.font = {
      bold: def.bold,
      italic: def.italic,
      size: def.fontSize ?? 11,
      name: def.fontName ?? 'Calibri',
      color: def.fontColor ? { argb: def.fontColor } : undefined
    };

    if (def.fill) {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: def.fill } };
    }

    if (def.horizontal || def.vertical || def.wrapText) {
      cell.alignment = {
        horizontal: def.horizontal,
        vertical: def.vertical,
        wrapText: def.wrapText
      };
    }

    if (def.border) {
      const side = (style?: string) => (style ? { style: style as import('exceljs').BorderStyle } : undefined);
      cell.border = {
        top: side(def.border.top),
        bottom: side(def.border.bottom),
        left: side(def.border.left),
        right: side(def.border.right)
      };
    }
  }

  // Reads the first worksheet of an uploaded .xlsx/.xls file into an array of
  // row objects keyed by the header cells. Values are coerced to trimmed
  // strings so callers can compare them without worrying about numeric cells.
  public async readAsExcelFile(file: File): Promise<Record<string, string>[]> {
    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(buffer, { type: 'array' });
    const firstSheetName = workbook.SheetNames[0];
    if (!firstSheetName) {
      return [];
    }
    const worksheet = workbook.Sheets[firstSheetName];
    const raw = XLSX.utils.sheet_to_json<Record<string, unknown>>(worksheet, { defval: '' });
    return raw.map((row) => this.stringifyRow(row));
  }

  private stringifyRow(row: Record<string, unknown>): Record<string, string> {
    const out: Record<string, string> = {};
    for (const [key, value] of Object.entries(row)) {
      out[key.trim()] = this.cellToString(value);
    }
    return out;
  }

  private cellToString(value: unknown): string {
    if (value == null) {
      return '';
    }
    if (typeof value === 'object') {
      return value instanceof Date ? value.toISOString() : '';
    }
    return String(value).trim();
  }

  private saveAsExcelFile(buffer: any, fileName: string): void {
    const data: Blob = new Blob([buffer], {type: EXCEL_TYPE});
    FileSaver.saveAs(data, fileName + '_export_' + new  Date().getTime() + EXCEL_EXTENSION);
 }
}
